import { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react'
import { api } from '../api/client'
import { cacheLangForUnauth } from '../i18n/I18nContext'
import { type Lang, isValidLang, DEFAULT_LANG } from '../i18n/languages'
import { applyUserTheme, THEME_COLORS_PREF_KEY } from '../utils/userTheme'
import { applySkin, clearSkin, type PluginView } from '../utils/skin'
import { useTheme } from './ThemeContext'

interface User {
  id: number
  username: string
  role: string
  is_active: boolean
  ai_quota: number
  api_credit: number
  agent_bundle_credit: number
  file_quota_mb: number
  platform_gifted_credit: number
  total_effective: number
  has_api_key: boolean
  api_key_last4: string
  timezone: string
  language: string
  ui_prefs: Record<string, any>
  avatar_url: string | null
  bio: string | null
  status_text: string | null
  status_color: string | null
  setup_completed: boolean
  created_at: string | null
  assigned_pool_key_name: string | null  // v0.1.5: 绑定的池 Key 名
  email: string | null  // v0.2.0 邮箱
  email_verified: boolean  // v0.2.0 邮箱是否已验证
}

interface LoginOptions {
  method?: string
  code?: string
}

interface RegisterOptions {
  email?: string
  code?: string
}

interface AuthContextType {
  user: User | null
  loading: boolean
  login: (loginId: string, password: string, options?: LoginOptions) => Promise<void>
  register: (username: string, password: string, options?: RegisterOptions) => Promise<void>
  logout: () => void
  refreshUser: () => Promise<void>
  rebindEmail: (email: string, code: string) => Promise<void>
  removeEmail: () => Promise<void>
}

const AuthContext = createContext<AuthContextType | null>(null)

// 纯函数：从登录/注册响应构建 User 对象（模块级，引用稳定）
function buildUserFromData(data: any): User {
  return {
    id: data.user_id,
    username: data.username,
    role: data.role,
    is_active: true,
    ai_quota: 0,
    api_credit: 0,
    has_api_key: false,
    api_key_last4: '',
    timezone: 'Asia/Shanghai',
    language: isValidLang(data.language) ? data.language : DEFAULT_LANG,
    ui_prefs: data.ui_prefs ?? {} as Record<string, any>,
    agent_bundle_credit: 0,
    file_quota_mb: 100,
    platform_gifted_credit: 0,
    total_effective: 0,
    avatar_url: null,
    bio: null,
    status_text: null,
    status_color: null,
    setup_completed: data.setup_completed ?? true,
    created_at: null,
    assigned_pool_key_name: null,
    email: data.email ?? null,
    email_verified: data.email_verified ?? false,
  }
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)
  const { theme } = useTheme()

  // 同步一份到 localStorage（user_info）：世界块/沉浸界面等非 React 环境读取当前用户
  useEffect(() => {
    try {
      if (user) {
        localStorage.setItem('user_info', JSON.stringify(user))
      } else {
        localStorage.removeItem('user_info')
      }
    } catch { /* 存储不可用时忽略 */ }
  }, [user])

  // 主题与皮肤统一应用链：先自选色（ui_prefs.theme_colors），后皮肤插件覆盖（win）
  // 所有触发点都收敛到这里重算，避免 applyUserTheme / applySkin 互相清掉对方设置的变量
  // 回退保障：管理员全局关闭/插件卸载后皮肤自动回退——
  //   1) 立即：登录/刷新/主题切换/自选色保存（依赖变化触发）
  //   2) 轮询：每 60s 静默刷新（管理员关闭后 ≤60s 回退）
  //   3) 焦点恢复：页面从后台切回时立即刷新
  //   4) 事件：WS 广播 plugins_changed / 本地切换皮肤或保存自选色 → 立即重算
  const applyThemeChain = useCallback(() => {
    if (!user) {
      applyUserTheme(null)
      clearSkin()
      return
    }
    applyUserTheme(user.ui_prefs?.[THEME_COLORS_PREF_KEY])
    api.safe.get<{ plugins: PluginView[] }>('/plugins')
      .then((res) => {
        if (!res.ok) { clearSkin(); return }
        const skins = (res.value.plugins || []).filter((p) => p.category === 'skin' && p.effective)
        const active = skins[0] || null
        applySkin(active?.id || null, active?.skin_vars, theme === 'dark')
      })
      .catch(() => clearSkin())
  }, [user?.id, user?.ui_prefs?.[THEME_COLORS_PREF_KEY], theme])

  useEffect(() => {
    applyThemeChain()
    const iv = setInterval(applyThemeChain, 60_000)
    const onVisible = () => { if (document.visibilityState === 'visible') applyThemeChain() }
    const onPluginsChanged = () => applyThemeChain()
    document.addEventListener('visibilitychange', onVisible)
    window.addEventListener('plugins-changed', onPluginsChanged)
    return () => {
      clearInterval(iv)
      document.removeEventListener('visibilitychange', onVisible)
      window.removeEventListener('plugins-changed', onPluginsChanged)
    }
  }, [applyThemeChain])

  const refreshUser = useCallback(async () => {
    try {
      const data = await api.get('/auth/me')
      setUser(data)
    } catch {
      setUser(null)
      localStorage.removeItem('access_token')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    const token = localStorage.getItem('access_token')
    if (token) {
      refreshUser()
    } else {
      setLoading(false)
    }
  }, [refreshUser])

  const login = useCallback(async (loginId: string, password: string, options?: LoginOptions) => {
    const body: any = {
      login_id: loginId,
      password: password || '',
      method: options?.method || 'direct',
    }
    if (options?.method === 'email_code' && options?.code) {
      body.verification_code = options.code
    }
    const data = await api.post('/auth/login', body)
    localStorage.setItem('access_token', data.access_token)
    cacheLangForUnauth(data.language as Lang)
    setUser(buildUserFromData(data))
  }, [])

  const register = useCallback(async (username: string, password: string, options?: RegisterOptions) => {
    const body: any = { username, password }
    if (options?.email) body.email = options.email
    if (options?.code) body.verification_code = options.code
    const data = await api.post('/auth/register', body)
    localStorage.setItem('access_token', data.access_token)
    cacheLangForUnauth(data.language as Lang)
    setUser(buildUserFromData(data))
  }, [])

  const rebindEmail = useCallback(async (email: string, code: string) => {
    const data = await api.put('/auth/email', { email, code })
    setUser(prev => prev ? { ...prev, email: data.email, email_verified: data.email_verified } : prev)
  }, [])

  const removeEmail = useCallback(async () => {
    const data = await api.delete('/auth/email')
    setUser(prev => prev ? { ...prev, email: data.email, email_verified: data.email_verified } : prev)
  }, [])

  const logout = useCallback(() => {
    localStorage.removeItem('access_token')
    setUser(null)
  }, [])

  const ctxValue = useMemo(() => ({
    user, loading, login, register, logout, refreshUser, rebindEmail, removeEmail,
  }), [user, loading, login, register, logout, refreshUser, rebindEmail, removeEmail])

  return (
    <AuthContext.Provider value={ctxValue}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used within AuthProvider')
  return ctx
}
